import React from 'react';

import { useTranslation } from 'react-i18next';

import { type BaseCurrencyType, type SupportedWalletType } from '@pkg/types';
import { convertWeiToEth, truncateContractAddress } from '@pkg/utils';

import { type Account } from '@app/graphql/codegen/graphql';

import { CurrencyChip } from '../CurrencyChip';
import { CurrencyIcon } from '../CurrencyIcon';
import { Thumbnail } from '../Thumbnail';
import { CopyTextAction } from '../actions';

import { useWalletOptionDetails } from './useWalletOptionDetails';

import {
    BalanceItem,
    BalanceList,
    ConnectedOption,
    CurrencyIconWrapper,
    DisconnectAction,
    DisconnectText,
    Interior,
    PrimaryColumn,
    PrimaryDetails,
    SecondaryColumn,
    SupportedCurrencyItem,
    SupportedCurrencyList,
    ThumbnailInner,
    ThumbnailOuter,
    WalletAction,
    WalletName,
} from './WalletOption.style';
import { tokens } from './WalletOption.tokens';

// TODO: We need a `mobile` variant to makes the `copy` and
// `disconnect` actions easier to tap.
export interface WalletOptionProps {
    type: SupportedWalletType;
    account?: Account;
    disabled?: boolean;
    onAction?: (type: SupportedWalletType) => void;
    onDisconnect?: (address: string) => void;
}

export function WalletOption({ type, account, disabled = false, onAction, onDisconnect }: WalletOptionProps) {
    const [translate] = useTranslation('wallet');
    const { name, image, currencies } = useWalletOptionDetails(type);

    const connected = Boolean(account?.address?.length);

    const primaryContent =
        connected && account?.address ? (
            <PrimaryDetails>
                <CopyTextAction
                    text={account?.address}
                    uniqueLabel={truncateContractAddress(account?.address)}
                    aria-label={translate('address-copy-cta-a11y')}
                />

                <DisconnectAction
                    aria-label={translate('address-disconnect-cta-a11y')}
                    onClick={() => onDisconnect?.(account?.address)}
                >
                    <DisconnectText>{translate('address-disconnect-cta')}</DisconnectText>
                </DisconnectAction>
            </PrimaryDetails>
        ) : (
            name && <WalletName>{name}</WalletName>
        );

    const supportedItems = currencies.map((currency) => (
        <SupportedCurrencyItem key={`SupportedCurrencyItem-${currency}`}>
            <CurrencyIconWrapper>
                <CurrencyIcon currency={currency} />
            </CurrencyIconWrapper>
        </SupportedCurrencyItem>
    ));

    const supportedListMarkup =
        supportedItems.length && !connected ? <SupportedCurrencyList>{supportedItems}</SupportedCurrencyList> : null;

    const balanceItems = Object.keys(account?.coins ?? {}).map((coin) => {
        const coinData = account?.coins[coin];

        return coinData.amount > 0 && currencies.includes(coinData?.symbol?.toLowerCase()) ? (
            <BalanceItem key={`BalanceItem-${coin}`}>
                <CurrencyChip
                    currency={coin as BaseCurrencyType}
                    value={convertWeiToEth(parseFloat(coinData.amount || '0'), 2)}
                    maxLength={tokens.balance.maxLength}
                />
            </BalanceItem>
        ) : undefined;
    });

    // TODO: We should have an empty state for "no balances".
    const balanceListMarkup =
        Object.keys(account?.coins ?? {}).length && connected ? <BalanceList>{balanceItems}</BalanceList> : null;

    const interiorMarkup = (
        <Interior>
            <PrimaryColumn>
                <ThumbnailOuter $connected={connected}>
                    <ThumbnailInner>
                        <Thumbnail imageUrl={image} />
                    </ThumbnailInner>
                </ThumbnailOuter>

                {primaryContent}
            </PrimaryColumn>

            <SecondaryColumn>
                {supportedListMarkup}
                {balanceListMarkup}
            </SecondaryColumn>
        </Interior>
    );

    return connected ? (
        <ConnectedOption>{interiorMarkup}</ConnectedOption>
    ) : (
        <WalletAction
            aria-label={translate('address-connect-cta-a11y')}
            disabled={disabled}
            onClick={() => onAction?.(type)}
        >
            {interiorMarkup}
        </WalletAction>
    );
}
